import { rgbaToThumbHash, thumbHashToRGBA } from 'thumbhash'

function bytesToBase64(bytes) {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

function base64ToBytes(value) {
  const binary = atob(String(value || ''))
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}

export function thumbhashFromRgba(width, height, rgba) {
  const w = Math.max(1, Math.min(100, Math.round(Number(width) || 0)))
  const h = Math.max(1, Math.min(100, Math.round(Number(height) || 0)))
  if (!rgba || rgba.length !== w * h * 4) return null
  try { return bytesToBase64(rgbaToThumbHash(w, h, rgba)) } catch { return null }
}

export function decodeThumbhash(value) {
  if (typeof value !== 'string' || !value || value.length > 128) return null
  try {
    const decoded = thumbHashToRGBA(base64ToBytes(value))
    if (!decoded || decoded.w < 1 || decoded.h < 1 || decoded.w > 100 || decoded.h > 100) return null
    if (decoded.rgba?.length !== decoded.w * decoded.h * 4) return null
    return decoded
  } catch {
    return null
  }
}

export function thumbhashFromCanvas(source) {
  if (!source || typeof document === 'undefined') return null
  const sourceWidth = Number(source.videoWidth || source.naturalWidth || source.width) || 0
  const sourceHeight = Number(source.videoHeight || source.naturalHeight || source.height) || 0
  if (!sourceWidth || !sourceHeight) return null
  const scale = Math.min(100 / sourceWidth, 100 / sourceHeight, 1)
  const width = Math.max(1, Math.round(sourceWidth * scale))
  const height = Math.max(1, Math.round(sourceHeight * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return null
  try {
    context.drawImage(source, 0, 0, width, height)
    return thumbhashFromRgba(width, height, context.getImageData(0, 0, width, height).data)
  } catch {
    return null
  }
}

export async function thumbhashFromBlob(blob) {
  if (!(blob instanceof Blob)) return null
  if (typeof createImageBitmap === 'function') {
    let bitmap = null
    try {
      bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' })
      return thumbhashFromCanvas(bitmap)
    } catch { /* SVG and older WebKit use the image fallback below. */ }
    finally { try { bitmap?.close?.() } catch {} }
  }
  if (typeof document === 'undefined') return null
  const url = URL.createObjectURL(blob)
  const image = new Image()
  try {
    await new Promise((resolve, reject) => {
      image.onload = resolve
      image.onerror = () => reject(new Error('warm_decode_failed'))
      image.src = url
    })
    return thumbhashFromCanvas(image)
  } catch {
    return null
  } finally {
    URL.revokeObjectURL(url)
  }
}

// Memoized "does this thumbhash decode?" check. The canvas placeholder no
// longer renders the blur (see warmPlaceholderTexture), but mount/readiness
// gating still keys off thumbhash validity — keep that contract cheap.
const warmValidity = new Map()

export function hasWarmData(value) {
  if (typeof value !== 'string' || !value || value.length > 128) return false
  let known = warmValidity.get(value)
  if (known === undefined) {
    known = decodeThumbhash(value) !== null
    warmValidity.set(value, known)
  }
  return known
}

// Shared solid-gray placeholder texture, one per resolved theme color. The
// thumbhash blur reconstruction reads as a per-image color patch that visibly
// "变色" when the real texture lands (tone/ICC shift) — a uniform neutral from
// the design tokens can't shift, and the fade-in on texture apply carries the
// transition instead.
const placeholderTextureCache = new Map()

export function warmPlaceholderTexture(PIXI = globalThis.PIXI) {
  if (!PIXI?.Texture || typeof document === 'undefined') return null
  let color = ''
  try { color = getComputedStyle(document.documentElement).getPropertyValue('--surface-2').trim() } catch {}
  if (!color) color = '#E6E6E4'
  const cached = placeholderTextureCache.get(color)
  if (cached && !cached.destroyed) return cached
  const canvas = document.createElement('canvas')
  canvas.width = 8
  canvas.height = 8
  const context = canvas.getContext('2d')
  if (!context) return null
  context.fillStyle = color
  context.fillRect(0, 0, 8, 8)
  try {
    const texture = PIXI.Texture.from(canvas)
    placeholderTextureCache.set(color, texture)
    return texture
  } catch {
    return null
  }
}

